import React, { FC, useMemo } from 'react'
import { Box, Typography } from 'transfix-ui/core'
import { IShipment, IShipperLoadTypes } from 'types'
import { AdditionalOptions } from './AdditionalOptions'
import { BulletGroup } from './BulletGroup'
import { LoadUnload } from './LoadUnload'
import { Securing } from './Securing'
import { Temperature } from './Temperature'

export interface IEquipmentProps {
  shipment: IShipment
}

export const LOADING_TYPES_MAP = {
  [IShipperLoadTypes.palletized]: 'Palletized',
  [IShipperLoadTypes.floorLoad]: 'Floor Load',
  [IShipperLoadTypes.bulkLoading]: 'Bulk Loading',
}

export const Equipment: FC<IEquipmentProps> = ({ shipment }) => {
  const { equipment, info } = shipment || {}
  const { attributes, name, parentKey } = equipment || {}
  const {
    doorType,
    floorType,
    flow,
    liftGate,
    loadBars,
    loadLocks,
    maxTempControl,
    minTempControl,
    palletJack,
    precool,
    roofType,
    straps,
    tempControlType,
    tracks,
    wallType,
  } = attributes || {}
  const hasNoSecuring = !straps && !loadBars && !loadLocks && !tracks
  const hasNoAdditional = !floorType && !doorType && !wallType && !roofType
  const hasNoLoadUnload = !palletJack && !liftGate

  const { carrierSpecialInstructions, loadingType } = info || {}
  const loadingTypeLabel = loadingType ? LOADING_TYPES_MAP[loadingType] : null

  const showTemperature = !(!precool && !tempControlType && !minTempControl && !maxTempControl)
  const flowAndTemperatureItems = useMemo(
    () => [
      {
        id: 'flow',
        show: !!flow,
        component: (
          <Box key='flow' component='span'>
            {flow?.includes('Cont') ? 'Continous Flow' : ''}
            {flow?.includes('Start') ? 'Start/Stop Flow' : ''}
          </Box>
        ),
      },
      {
        id: 'temperature',
        show: showTemperature,
        component: <Temperature key='temperature' attributes={attributes} />,
      },
    ],
    [flow, showTemperature, attributes]
  )
  const moreInfoItems = useMemo(
    () => [
      {
        id: 'securing',
        show: !hasNoSecuring,
        component: <Securing key='securing' attributes={attributes} />,
      },
      {
        id: 'additionalOptions',
        show: !hasNoAdditional,
        component: <AdditionalOptions key='additionalOptions' attributes={attributes} />,
      },
      {
        id: 'loadUnload',
        show: !hasNoLoadUnload,
        component: <LoadUnload key='loadUnload' attributes={attributes} />,
      },
      {
        id: 'loadingType',
        show: !!loadingTypeLabel,
        component: (
          <Box key='loadingType' component='span'>
            {loadingTypeLabel}
          </Box>
        ),
      },
      {
        id: 'reefer',
        show: parentKey === 'reefer',
        component: (
          <Box key='reefer' component='span'>
            Can load reefer
          </Box>
        ),
      },
    ],
    [hasNoSecuring, hasNoAdditional, hasNoLoadUnload, loadingTypeLabel, parentKey, attributes]
  )

  return (
    <Box mt={2} p={3} bgcolor='#F3F5F7'>
      <Box>
        <Typography color='textSecondary' variant='subtitle2'>
          Equipment
        </Typography>
        <Box>{name}</Box>
        <Box>
          <BulletGroup items={flowAndTemperatureItems} />
        </Box>
        <Box component='span'>
          <BulletGroup items={moreInfoItems} />
        </Box>
      </Box>
      <Box mt={3}>
        <Typography color='textSecondary' variant='subtitle2'>
          Carrier Instructions
        </Typography>
        <Box mt={1}>{carrierSpecialInstructions}</Box>
      </Box>
      <Box mt={3}>
        <Typography color='textSecondary' variant='subtitle2'>
          Special Instructions
        </Typography>
        <Box mt={1}>
          <Typography color='textPrimary' variant='subtitle2'>
            PPE Requirement
          </Typography>
          <Box>
            Carriers are required to comply with Shipper specific PPE policies and procedures and
            PPE requirements as required by law.
          </Box>
        </Box>
        <Box mt={3}>
          <Typography color='textPrimary' variant='subtitle2'>
            Tracking is required
          </Typography>
          <Box>See Shipment Term #1</Box>
        </Box>
      </Box>
    </Box>
  )
}
